import React, {useEffect, useRef, useState} from 'react';
import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {isEmpty} from 'lodash';
import {useTranslation} from 'react-i18next';

/**
 * 引数
 */
interface Props {
  title: string;
  source: any;
  y: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  series?: string;
  stacked?: boolean; //付与する事で積み上げになります
  grid?: {
    top?: number | string;
    bottom?: number | string;
    left?: number | string;
    right?: number | string;
  };
  color?: string | {};
  height?: string;
  exportData?: any;
  exportFields?: any;
  exportFilename?: string;
  xList: any;
  chartToggleStatus?: boolean; // チャート切替ボタン表示フラグ
  isParetoMode?: boolean; // パレートモード表示フラグ
  onChartToggle?: () => void; // チャート切替処理
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
}

/**
 * LFCChartsBarYMD
 * @param props
 * @returns
 */
const LFCChartsBarYMD2 = React.memo((props: Props) => {
  const {t} = useTranslation();
  const renderFlgRef = useRef(false);

  type DsRow = {source?: []; transform?: any};
  const [dataset, setDataset] = useState<DsRow[]>([]);
  const [series, setSeries] = useState([]);

  // State for tracking the total sum of failure counts
  const [totalSum, setTotalSum] = useState<number>(0);

  useEffect(() => {
    // Reset the render flag when isParetoMode changes to ensure re-rendering
    if (props.isParetoMode === undefined || props.isParetoMode === false) {
      renderFlgRef.current = true;
    }

    // データソースクリア
    if (props.source.length === 0) {
      setSeries([]);
      setDataset([]);
      setTotalSum(0); // Reset total sum when data source is empty
      return;
    }

    // Calculate total sum of failure counts
    const calculatedTotalSum = props.source.reduce((sum, item) => {
      // Get the value from the column specified in props.y.dsColumn (usually 'ng' for failure count)
      const value = Number(item[props.y.dsColumn]) || 0;
      return sum + value;
    }, 0);

    // Set the total sum state
    setTotalSum(calculatedTotalSum);

    // データソース生成
    const l_ds: DsRow[] = [];
    const l_series: any = [];
    l_ds.push({source: props.source});

    if (props.series) {
      // シリーズ指定あり
      let strSeries: string = props.series;
      const grpNames = Array.from(new Set(props.source.map((obj: any) => obj[strSeries])));

      // シリーズ設定
      grpNames.forEach((grp, i) => {
        l_ds.push({
          transform: {
            type: 'filter',
            config: {dimension: props.series, value: grp}
          }
        });
        l_series.push({
          type: 'bar',
          name: grp,
          stack: props.stacked ? 'total' : null,
          encode: {y: props.y.dsColumn},
          datasetIndex: i + 1
        });
      });
    } else {
      // シリーズ指定なし
      l_series.push({
        type: 'bar',
        encode: {y: props.y.dsColumn}
      });
    }
    setSeries(l_series);
    setDataset(l_ds);
  }, [props.source, props.series, props.y.dsColumn, props.isParetoMode, props.stacked]);

  return (
    <LFCBaseCharts
      option={{
        title: {
          text: props.title
        },
        grid: props.grid
          ? props.grid
          : {
              top: '40',
              left: '60',
              right: '10',
              bottom: '93'
            },
        ...(props.series
          ? {
              legend: {type: 'scroll', bottom: 0, itemWidth: 10}
            }
          : {
              legend: {
                show: false
              }
            }),
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            let tmp: string = '';
            if (params.data.map !== undefined) {
              tmp =
                params.data.lq_time +
                '<br/>' +
                t('部位') +
                '： ' +
                params.data.map +
                '<br/>' +
                t('不良数') +
                '： <span style="color: #FF0000">' +
                params.data.ng +
                '</span><br/>';
            } else if (params.data.work !== undefined) {
              tmp =
                params.data.lq_time +
                '<br/>' +
                t('機種') +
                '： ' +
                params.data.work +
                '<br/>' +
                t('不良数') +
                '： <span style="color: #FF0000">' +
                params.data.ng +
                '</span><br/>';
            } else if (params.data.job_type_name !== undefined) {
              tmp =
                params.data.lq_time +
                '<br/>' +
                t('検査手法') +
                '： ' +
                params.data.job_type_name +
                '<br/>' +
                t('不良数') +
                '： <span style="color: #FF0000">' +
                params.data.ng +
                '</span><br/>';
            }
            return tmp;
          }
        },
        dataZoom: [
          {
            type: 'inside'
          }
        ],
        color: props.color ? props.color : null,
        dataset: dataset,
        xAxis: [
          {
            type: 'category',
            axisLabel: {
              rotate: '45',
              fontSize: 10
            },
            data: !isEmpty(props.xList)
              ? props.xList
              : props.source.map((a: any) => a.id).filter((b: any) => b !== undefined)
          }
        ],
        yAxis: [
          {
            type: props.y.type,
            name: '', // Y軸名を非表示化
            axisLabel: {
              fontSize: 10,
              color: '#FF0000' // 不良数のY軸ラベルを赤色に
            },
            nameTextStyle: {
              color: '#FF0000' // 軸名も赤色に
            }
          }
        ],
        series: series
      }}
      exportData={props.exportData}
      exportFields={props.exportFields}
      exportFilename={props.exportFilename}
      height={props.height}
      chartToggleStatus={props.chartToggleStatus}
      isParetoMode={props.isParetoMode}
      onChartToggle={props.onChartToggle}
      totalFailureCount={totalSum} // Pass total failure count for header display
      zoomCharts={props.zoomCharts ? props.zoomCharts : undefined}
      zoomChartsClose={props.zoomChartsClose ? props.zoomChartsClose : undefined}
      zoomChartId={props.zoomChartId ? props.zoomChartId : undefined}
      zoomStatus={props.zoomChartId === props.zoomedChartId}
    />
  );
});
export default LFCChartsBarYMD2;
